import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import type * as Y from 'yjs';

import { type UserProfileIdentity } from '@canvas/atlassian-client/identity/types';
import { type Capabilities } from '@canvas/capability';
import { type Identity } from '@canvas/model/identity';

import { type CollabStrategy } from './collab-strategy';
import { type RemoteDocument } from './network/document';
import { type RelaySupplierResult, type SocketFactory } from './network/socket';
import { YjsSocket, type GetDocumentStatusFn, type Waiter } from './network/yjs-socket';
export type YjsSocketOptions = YjsSocketDetailedOptions | YjsSocketProviderOptions;

interface YjsSocketProviderOptions {
  type: 'provider';
  provider: YjsSocket;
  identitySupplier: () => Promise<Identity>;
  userSupplier:
    | ((accountIds: readonly string[]) => Promise<(UserProfileIdentity | undefined)[]>)
    | null;
}

interface YjsSocketDetailedOptions {
  type?: 'detailed';
  existingDocument: Y.Doc;
  accessTokenSupplier: () => Promise<string>;
  relaySupplier: (
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ) => RelaySupplierResult;
  identitySupplier: () => Promise<Identity>;
  userSupplier:
    | ((accountIds: readonly string[]) => Promise<(UserProfileIdentity | undefined)[]>)
    | null;
  getDocument: (signal: AbortSignal) => Promise<RemoteDocument>;
  persistLargeStep: (step: Uint8Array<ArrayBuffer>, signal: AbortSignal) => Promise<void>;
  reconcileOfflineChanges?: (remoteDocument: RemoteDocument) => Promise<void>;
  ignoreOrigins?: any[];
  debounceWaiter?: Waiter;
  socketFactory?: SocketFactory;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  canSync?: boolean;
  canUpdate?: boolean;
  boardId: string;
  indexedDbEnabled?: boolean;
  getRemoteStepsUpdate: (
    step: string,
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ) => Promise<Uint8Array[]>;
  getDocumentStatus?: GetDocumentStatusFn;
  capabilitiesProvider?: () => Capabilities | undefined;
  /**
   * Specifies a specific version of the document to load.
   */
  versionId?: string;
  /**
   * Determines whether or not version history is enabled for whiteboards or databases
   */
  useVersionHistory: boolean;
}

export function createYjsSocket(options: YjsSocketOptions): YjsSocket {
  let provider: YjsSocket;

  if (options.type === 'provider') {
    provider = options.provider;
  } else {
    provider = new YjsSocket({
      accessTokenSupplier: options.accessTokenSupplier,
      relaySupplier: options.relaySupplier,
      doc: options.existingDocument,
      getDocument: options.getDocument,
      persistLargeStep: options.persistLargeStep,
      ignoreOrigins: options.ignoreOrigins ?? [],
      debounceWaiter: options.debounceWaiter,
      socketFactory: options.socketFactory,
      getAnalyticsWebClient: options.getAnalyticsWebClient,
      canSync: options.canSync,
      canUpdate: options.canUpdate,
      indexedDbEnabled: options.indexedDbEnabled,
      boardId: options.boardId,
      getRemoteStepsUpdate: options.getRemoteStepsUpdate,
      getDocumentStatus: options.getDocumentStatus,
      getCapabilities: options.capabilitiesProvider,
      versionId: options.versionId,
      reconcileOfflineChanges: options.reconcileOfflineChanges,
      useVersionHistory: options.useVersionHistory,
    });
  }

  return provider;
}
